import assistantsService from "../services/assistants.service";
import { readList, readPagination } from "../utils/api";
import { readKnowledgeStatus } from "../utils/knowledgeStatus";
import {
  ASSISTANT_LOADING,
  ASSISTANT_ERROR,
  SET_ASSISTANT,
  SET_ASSISTANTS,
  ASSISTANTS_LOADING,
  SET_ACTIVE_ASSISTANT,
} from "./type";

// Which assistant the sidebar switcher currently points at. Persisted so the
// choice survives a refresh.
export const ACTIVE_ASSISTANT_KEY = "activeAssistantId";

// Assistants the API has said are gone - trashed here, or deleted from
// somewhere else - for as long as this page is open.
//
// Without this the app can chase its own tail: a page reads the active
// assistant, the API answers 404, the id is cleared, and the switcher picks
// the first assistant in the list it has - which, if that list still carries
// the one just removed, is the same id again. Every pass is another round of
// requests, and nothing ever settles.
const goneAssistants = new Set();

export const markAssistantGone = (id) => {
  if (id) goneAssistants.add(id);
};

export const isAssistantGone = (id) => goneAssistants.has(id);

/** The assistants worth pointing at: whatever the list holds, minus those. */
export const livingAssistants = (list = []) =>
  list.filter((item) => !goneAssistants.has(item?.id));

// The API wraps its payload inconsistently, so check the usual spots.
const readAssistant = (resp) => resp?.data?.data || resp?.data || null;

// The collection may come back bare or wrapped under data/assistants.
const readAssistants = (resp) => {
  const body = resp?.data;

  const candidates = [
    body,
    body?.data,
    body?.assistants,
    body?.data?.assistants,
    body?.results,
  ];

  return candidates.find(Array.isArray) || [];
};

// Failures come back as { error }, successes-with-a-note as { message }.
const readMessage = (resp, fallback) =>
  resp?.data?.data?.error ||
  resp?.data?.error ||
  resp?.data?.data?.message ||
  resp?.data?.message ||
  fallback;

/**
 * POST /assistants — crawling starts automatically, there's no separate
 * ingestion call to make afterwards.
 * @param {{ sourceUrl: string, name: string }} payload
 */
export const createAssistant = (payload) => async (dispatch) => {
  dispatch({ type: ASSISTANT_LOADING, payload: true });

  try {
    const resp = await assistantsService.assistants(payload);

    // Creation answers 201; some environments still return 200.
    if (resp?.status === 200 || resp?.status === 201) {
      const assistant = readAssistant(resp);
      dispatch({ type: SET_ASSISTANT, payload: assistant });
      // The app points at the assistant that was just made, from the moment it
      // exists: the onboarding steps, and every page reached before they are
      // finished, read activeAssistantId rather than this response.
      if (assistant?.id) dispatch(setActiveAssistant(assistant.id));
      dispatch({ type: ASSISTANT_ERROR, payload: "" });
      return { success: true, response: resp, assistant };
    }

    const message = readMessage(
      resp,
      resp?.status === 401
        ? "Your session expired. Sign in again to continue."
        : "We couldn't create your assistant. Please try again.",
    );

    dispatch({ type: ASSISTANT_ERROR, payload: message });
    return { success: false, response: resp, message };
  } finally {
    dispatch({ type: ASSISTANT_LOADING, payload: false });
  }
};

/**
 * GET /assistants — every assistant on the account, for the Assistant
 * section's list.
 */
export const listAssistants = () => async (dispatch) => {
  dispatch({ type: ASSISTANT_LOADING, payload: true });
  dispatch({ type: ASSISTANTS_LOADING, payload: true });

  try {
    const resp = await assistantsService.getAssistants();

    if (resp?.status === 200) {
      const assistants = readAssistants(resp);
      dispatch({ type: SET_ASSISTANTS, payload: assistants });
      dispatch({ type: ASSISTANT_ERROR, payload: "" });
      return { success: true, response: resp, assistants };
    }

    const message = readMessage(
      resp,
      resp?.status === 401
        ? "Your session expired. Sign in again to continue."
        : "We couldn't load your assistants. Please try again.",
    );

    dispatch({ type: ASSISTANT_ERROR, payload: message });
    return { success: false, response: resp, message, assistants: [] };
  } finally {
    dispatch({ type: ASSISTANT_LOADING, payload: false });
    dispatch({ type: ASSISTANTS_LOADING, payload: false });
  }
};

/**
 * GET /assistants/:id — returns the assistant record directly as the body:
 * { id, organizationId, name, sourceUrl, welcomeMessage, status,
 *   lastCrawlError, allowedDomain, widgetTheme, createdAt, updatedAt }
 * @param {string} assistId
 */
/**
 * GET /assistants/:id/knowledge-status - what the crawl is doing, and what it
 * found once it is done. Polled every few seconds while `isLearning` is true.
 *
 * Read-only, so the result is returned rather than stored: two screens show it
 * and neither needs the other's copy.
 * @param {string} assistId
 */
export const getKnowledgeStatus = (assistId) => async () => {
  if (!assistId) return { success: false, message: "No assistant to check." };

  const resp = await assistantsService.getKnowledgeStatus(assistId);

  if (resp?.status === 200) {
    return {
      success: true,
      response: resp,
      status: readKnowledgeStatus(resp?.data?.data || resp?.data),
    };
  }

  return {
    success: false,
    response: resp,
    notFound: resp?.status === 404,
    message: readMessage(resp, "We couldn't check your assistant's progress."),
  };
};

/**
 * POST /assistants/:id/recrawl - queues a fresh read of the site. Returns the
 * updated record, so the caller can start polling from its "crawling" status
 * without a round trip to fetch it.
 */
export const recrawlAssistant = (assistId) => async (dispatch) => {
  if (!assistId) {
    return { success: false, message: "No assistant id to recrawl." };
  }

  try {
    const resp = await assistantsService.recrawlAssistant(assistId);

    // Queued answers 201; some environments still return 200.
    if (resp?.status === 200 || resp?.status === 201) {
      const assistant = readAssistant(resp);
      dispatch({ type: SET_ASSISTANT, payload: assistant });
      return { success: true, response: resp, assistant };
    }

    return {
      success: false,
      response: resp,
      // 403 is the plan's monthly re-crawl allowance, 409 a crawl already
      // running. Both come back with a sentence of their own; these are only
      // read when one does not.
      message: readMessage(
        resp,
        resp?.status === 403
          ? "You've used all the re-crawls your plan allows for this assistant this month."
          : resp?.status === 409
            ? "A crawl is already running for this assistant."
            : "We couldn't start a recrawl.",
      ),
    };
  } catch (error) {
    return {
      success: false,
      message: error?.message || "We couldn't start a recrawl.",
    };
  }
};

/**
 * GET /assistants/:id/installation-status - what the backend can actually see
 * of the widget on the customer's site, rather than what we can infer from the
 * assistant record. Deliberately not stored in redux: it's a point-in-time
 * check that the card re-runs on demand.
 */
export const getInstallationStatus = (assistId) => async () => {
  if (!assistId) {
    return { success: false, message: "No assistant id to check." };
  }

  try {
    const resp = await assistantsService.getInstallationStatus(assistId);

    if (resp?.status === 200) {
      return { success: true, response: resp, status: readAssistant(resp) };
    }

    return {
      success: false,
      response: resp,
      message: readMessage(resp, "We couldn't check your installation."),
    };
  } catch (error) {
    return {
      success: false,
      message: error?.message || "We couldn't check your installation.",
    };
  }
};

export const getAssistantById = (assistId) => async (dispatch) => {
  if (!assistId) {
    return { success: false, message: "No assistant id to load." };
  }

  dispatch({ type: ASSISTANT_LOADING, payload: true });

  try {
    const resp = await assistantsService.getAssistantsId(assistId);

    if (resp?.status === 200) {
      const assistant = readAssistant(resp);
      dispatch({ type: SET_ASSISTANT, payload: assistant });
      dispatch({ type: ASSISTANT_ERROR, payload: "" });
      return { success: true, response: resp, assistant };
    }

    // Gone rather than broken: remembered so the switcher stops offering it.
    if (resp?.status === 404) markAssistantGone(assistId);

    // 402 is the account being paused rather than anything about this
    // assistant: the page says so and offers what still works.
    const code = resp?.data?.code || "";

    const message = readMessage(
      resp,
      resp?.status === 404
        ? "That assistant is no longer available."
        : "We couldn't load your assistant. Please try again.",
    );

    dispatch({ type: ASSISTANT_ERROR, payload: message });
    return {
      success: false,
      response: resp,
      notFound: resp?.status === 404,
      code,
      paused:
        resp?.status === 402 &&
        ["payment_required", "subscription_ended"].includes(code),
      message,
    };
  } finally {
    dispatch({ type: ASSISTANT_LOADING, payload: false });
  }
};

/**
 * GET /assistants/:id/config — the public widget config, as the live widget
 * sees it: { id, publicId, name, tagline, welcomeMessage, theme, suggestedPrompts }.
 * Doesn't touch the assistant store; the caller owns the result.
 * @param {string} assistId
 */
export const getAssistantConfig = (assistId) => async () => {
  if (!assistId) {
    return { success: false, message: "No assistant id to load config for." };
  }

  const resp = await assistantsService.getAssistantConfig(assistId);

  if (resp?.status === 200) {
    return { success: true, response: resp, config: readAssistant(resp) };
  }

  return {
    success: false,
    response: resp,
    message: readMessage(resp, "We couldn't load this assistant's settings."),
  };
};

/**
 * GET /assistants/:id/overview?from=&to= — dashboard metrics for a date
 * window. Read-only, so the result is returned rather than stored.
 * @param {string} assistId
 * @param {{ from: string, to: string }} range both YYYY-MM-DD
 */
export const getAssistantOverview = (assistId, range) => async (dispatch) => {
  if (!assistId) {
    return { success: false, message: "No assistant id to load metrics for." };
  }

  const resp = await assistantsService.getAssistantOverview(assistId, range);

  if (resp?.status === 200) {
    return { success: true, response: resp, overview: readAssistant(resp) };
  }

  // A 404 means the stored assistant is gone or belongs to another account.
  // Clearing it lets the sidebar pick a valid one instead of every page
  // reporting "not found" against an id that will never resolve.
  if (resp?.status === 404) {
    markAssistantGone(assistId);
    dispatch(setActiveAssistant(""));
    return { success: false, response: resp, notFound: true, message: "" };
  }

  return {
    success: false,
    response: resp,
    message: readMessage(
      resp,
      resp?.status === 401
        ? "Your session expired. Sign in again to continue."
        : "We couldn't load your dashboard.",
    ),
  };
};

/**
 * GET /assistants/:id/conversations — the conversation list.
 * @param {string} assistId
 * @param {object} [params] passed through as the query string
 */
export const listConversations = (assistId, params) => async () => {
  if (!assistId) {
    return { success: false, message: "No assistant id.", conversations: [] };
  }

  const resp = await assistantsService.getConversations(assistId, params);

  if (resp?.status === 200) {
    return {
      success: true,
      response: resp,
      conversations: readList(resp),
      pagination: readPagination(resp, params?.limit),
    };
  }

  return {
    success: false,
    response: resp,
    conversations: [],
    message: readMessage(
      resp,
      resp?.status === 401
        ? "Your session expired. Sign in again to continue."
        : "We couldn't load your conversations.",
    ),
  };
};

/**
 * GET /assistants/:id/conversations/:conversationId — one conversation,
 * with its messages.
 */
export const getConversationById =
  (assistId, conversationId) => async () => {
    if (!assistId || !conversationId) {
      return { success: false, message: "No conversation to load." };
    }

    const resp = await assistantsService.getConversationById(
      assistId,
      conversationId,
    );

    if (resp?.status === 200) {
      return { success: true, response: resp, conversation: readAssistant(resp) };
    }

    return {
      success: false,
      response: resp,
      message: readMessage(resp, "We couldn't load this conversation."),
    };
  };

/**
 * PATCH /assistants/:id — partial edit of name / tagline / allowedDomain /
 * widgetTheme. Send only the keys that changed.
 *
 * widgetTheme is one flat object: colours, typography, launcher, layout and
 * behaviour, plus welcomeMessage and suggestedPrompts, which live inside it
 * rather than at the top level.
 * @param {string} assistId
 * @param {{ name?: string, tagline?: string, allowedDomain?: string, widgetTheme?: object }} payload
 */
export const updateAssistant = (assistId, payload) => async (dispatch) => {
  if (!assistId) {
    return { success: false, message: "No assistant id to update." };
  }

  dispatch({ type: ASSISTANT_LOADING, payload: true });

  try {
    const resp = await assistantsService.UpdateAssistantsId(assistId, payload);

    if (resp?.status === 200) {
      const assistant = readAssistant(resp);
      dispatch({ type: SET_ASSISTANT, payload: assistant });
      dispatch({ type: ASSISTANT_ERROR, payload: "" });
      return { success: true, response: resp, assistant };
    }

    const message = readMessage(
      resp,
      "We couldn't save your changes. Please try again.",
    );

    dispatch({ type: ASSISTANT_ERROR, payload: message });
    return { success: false, response: resp, message };
  } finally {
    dispatch({ type: ASSISTANT_LOADING, payload: false });
  }
};

/**
 * DELETE /assistants/:id - moves the assistant to the trash. A soft delete:
 * it disappears from every list and its widget stops loading, and it stops
 * counting towards the plan's assistant allowance, which is what lets an
 * account shrink enough to take a smaller plan.
 *
 * The list is refetched rather than filtered locally, because the server
 * decides what is left - and the switcher reads that list.
 * @param {string} assistId
 */
export const deleteAssistant = (assistId) => async (dispatch) => {
  if (!assistId) return { success: false, message: "No assistant to remove." };

  const resp = await assistantsService.deleteAssistant(assistId);

  if (resp?.status === 200 || resp?.status === 204) {
    const stored = localStorage.getItem(ACTIVE_ASSISTANT_KEY);

    // The app cannot stay pointed at something that no longer exists; the
    // switcher picks a replacement from the refreshed list.
    if (stored === assistId) dispatch(setActiveAssistant(""));

    markAssistantGone(assistId);

    await dispatch(listAssistants());

    return { success: true, response: resp };
  }

  const message = readMessage(
    resp,
    resp?.status === 404
      ? "That assistant has already been removed."
      : "We couldn't remove that assistant.",
  );

  dispatch({ type: ASSISTANT_ERROR, payload: message });
  return { success: false, response: resp, message };
};

/**
 * Points the app at an assistant. Kept in redux so the pages react to the
 * sidebar switcher, and mirrored to localStorage so the choice survives a
 * refresh.
 * @param {string} id
 */
export const setActiveAssistant = (id) => (dispatch) => {
  if (id) {
    localStorage.setItem(ACTIVE_ASSISTANT_KEY, id);
  } else {
    localStorage.removeItem(ACTIVE_ASSISTANT_KEY);
  }

  dispatch({ type: SET_ACTIVE_ASSISTANT, payload: id || "" });
};

export const clearAssistantError = () => (dispatch) =>
  dispatch({ type: ASSISTANT_ERROR, payload: "" });



/**
 * GET /assistants/:id/conversations/:conversationId/messages — the transcript.
 * It's a separate endpoint from the conversation record, so the detail view
 * has to ask for both.
 */
export const getConversationMessages =
  (assistId, conversationId) => async () => {
    if (!assistId || !conversationId) {
      return { success: false, messages: [] };
    }

    const resp = await assistantsService.getConversationMessages(
      assistId,
      conversationId,
    );

    if (resp?.status === 200) {
      return { success: true, response: resp, messages: readList(resp) };
    }

    return {
      success: false,
      response: resp,
      messages: [],
      message: readMessage(resp, "We couldn't load this transcript."),
    };
  };

/**
 * POST /assistants/:id/memory/summarize — folds the facts gathered during
 * training into the assistant's memory summary.
 */
export const summarizeMemory = (assistId) => async () => {
  if (!assistId) return { success: false, message: "No assistant selected." };

  const resp = await assistantsService.summarizeMemory(assistId);
  const body = resp?.data?.data || resp?.data || {};

  if (resp?.status === 200 || resp?.status === 201) {
    return {
      success: true,
      response: resp,
      factsSummarized: body.factsSummarized ?? 0,
      memorySummary: body.memorySummary || "",
    };
  }

  return {
    success: false,
    response: resp,
    message: readMessage(resp, "We couldn't summarize what was learned."),
  };
};

/**
 * GET /assistants/:id/widget-state?visitorId= — the conversation this visitor
 * already has, so the tester picks up where it left off instead of starting
 * blank every reload.
 */
export const getWidgetState = (assistId, visitorId) => async () => {
  if (!assistId || !visitorId) return { success: false, conversation: null };

  const resp = await assistantsService.getWidgetState(assistId, visitorId);
  const body = resp?.data?.data || resp?.data || {};

  if (resp?.status === 200) {
    return {
      success: true,
      response: resp,
      conversation: body.conversation || null,
      // Set when the widget should not load at all: the account is paused for
      // an unpaid renewal or an ended subscription, or this assistant is over
      // the plan's allowance. The chat socket refuses these anyway - the flag
      // is what lets the caller skip the attempt and say why.
      paused: body.paused || null,
      // { period, used, limit, remaining, exceeded }. limit -1 is unlimited.
      quota: body.quota || null,
    };
  }

  // Non-fatal: the socket connection is still worth attempting.
  return { success: false, response: resp, conversation: null, paused: null, quota: null };
};

/** GET /assistants/:id/conversations/timeseries — daily counts for a window. */
export const getConversationTimeseries = (assistId, range) => async () => {
  if (!assistId) return { success: false, series: [] };

  const resp = await assistantsService.getConversationTimeseries(assistId, range);

  if (resp?.status === 200) {
    return { success: true, response: resp, series: readList(resp) };
  }

  return {
    success: false,
    response: resp,
    series: [],
    message: readMessage(resp, "We couldn't load your conversation trend."),
  };
};

/** GET /assistants/:id/conversations/topics — share percentages for a window. */
export const getConversationTopics = (assistId, range) => async () => {
  if (!assistId) return { success: false, topics: [] };

  const resp = await assistantsService.getConversationTopics(assistId, range);

  if (resp?.status === 200) {
    return { success: true, response: resp, topics: readList(resp) };
  }

  return {
    success: false,
    response: resp,
    topics: [],
    message: readMessage(resp, "We couldn't load your conversation topics."),
  };
};

/** GET /assistants/:id/conversations/:id/activity — the Activity timeline. */
export const getConversationActivity =
  (assistId, conversationId) => async () => {
    if (!assistId || !conversationId) {
      return { success: false, activity: [] };
    }

    const resp = await assistantsService.getConversationActivity(
      assistId,
      conversationId,
    );

    if (resp?.status === 200) {
      return { success: true, response: resp, activity: readList(resp) };
    }

    return {
      success: false,
      response: resp,
      activity: [],
      message: readMessage(resp, "We couldn't load this conversation's activity."),
    };
  };
